import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { useFlash } from 'contexts/flash';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { updateRoomLevelIssue } from 'services/startInspection';
import { loadRoomIssueById, WorkflowIssue } from 'services/report';
import { buildIssueAssignmentPutFields } from 'utils/issueAssignmentPutFields';
import { getIssuesConfig } from 'services/option';
import { DEFAULT_ISSUES_UI_CONFIG, IssuesUiConfig } from 'utils/issueDueDateColor';
import { DEFAULT_CRITICALITY_LEVELS } from 'utils/issueCriticality';
import { getApiErrorMessage } from 'utils/apiErrorMessage';
import type { IssueAssignmentFormValues } from '../components/IssueAssignmentFields';

export type RoomIssueFormValues = {
  description: string;
  status?: string;
  actionBy?: string;
  actionByDate?: string | null;
  resolutionDate?: string | null;
  validated?: string;
  [key: string]: unknown;
};

const RoomIssueFormSchema = yup.object({
  description: yup.string().required('Description is required'),
  status: yup.string().optional(),
  actionBy: yup.string().optional(),
  actionByDate: yup.string().nullable().optional(),
  resolutionDate: yup.string().nullable().optional(),
  validated: yup.string().optional()
});

type PhotoRow = {
  id: number;
  url: string;
  storage: { fileName: string; type: string };
};

export const useRoomIssueDialog = ({
  isOpen,
  issueId,
  submitIdHint,
  onSaved
}: {
  isOpen: boolean;
  issueId?: number;
  submitIdHint?: number;
  onSaved?: () => void;
}) => {
  const { setFlash } = useFlash();
  const { systemDropdownValues, singleEntryField } = useFilteredRoomContext();
  const [loading, setLoading] = useState(false);
  const [issue, setIssue] = useState<WorkflowIssue | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);
  const [newBeforePhotos, setNewBeforePhotos] = useState<File[]>([]);
  const [newAfterPhotos, setNewAfterPhotos] = useState<File[]>([]);
  const [archivedPhotoIds, setArchivedPhotoIds] = useState<number[]>([]);

  const legacyForm = useForm<RoomIssueFormValues>({
    resolver: yupResolver(RoomIssueFormSchema)
  });

  const assignmentForm = useForm<IssueAssignmentFormValues>({
    defaultValues: {
      assignedToUserId: '',
      dueDate: null,
      status: 'ACTIVE',
      criticalityKey: ''
    }
  });

  const criticalityLevels = issuesConfig.criticalityLevels?.length
    ? issuesConfig.criticalityLevels
    : DEFAULT_CRITICALITY_LEVELS;

  const submitId = issue?.submitId ?? submitIdHint;

  const loadIssue = useCallback(async () => {
    if (!issueId) return;
    setLoading(true);
    try {
      const data = await loadRoomIssueById(issueId, submitIdHint);
      const loaded = data.roomIssue;
      setIssue(loaded);
      setNewBeforePhotos([]);
      setNewAfterPhotos([]);
      setArchivedPhotoIds([]);

      const roomOptions: Record<string, unknown> = {};
      systemDropdownValues?.forEach((dropdownValue) => {
        roomOptions[`option_${dropdownValue.id}`] = '';
        roomOptions[`option_${dropdownValue.id}_name`] = '';
      });
      singleEntryField?.forEach((field) => {
        roomOptions[`single_${field.id}`] = '';
      });

      loaded.options?.forEach((issueOption) => {
        if (issueOption.fieldType === 'DROPDOWN') {
          systemDropdownValues?.forEach((dropdownValue) => {
            if (dropdownValue.id.toString() === issueOption.type) {
              const index = dropdownValue.dropdownValues.some(
                (dropdown) => dropdown.value === issueOption.value
              );
              roomOptions[`option_${issueOption.type}`] = index ? issueOption.value : 'OTHER';
              roomOptions[`option_${issueOption.type}_name`] = issueOption.value;
            }
          });
        } else {
          roomOptions[`single_${issueOption.type}`] = issueOption.value;
        }
      });

      legacyForm.reset({
        description: loaded.description || '',
        actionBy: loaded.actionBy || '',
        actionByDate: loaded.actionByDate,
        resolutionDate: loaded.resolutionDate as string | undefined,
        status: loaded.status,
        validated: (loaded as { validated?: boolean }).validated ? 'Yes' : 'No',
        ...roomOptions
      });

      assignmentForm.reset({
        assignedToUserId: loaded.assignedToUser?.id ? String(loaded.assignedToUser.id) : '',
        dueDate: loaded.dueDate ? dayjs(loaded.dueDate) : null,
        status: loaded.status || 'ACTIVE',
        criticalityKey: loaded.criticalityKey || loaded.criticality?.key || ''
      });
    } catch (err) {
      setFlash({
        type: 'error',
        message: getApiErrorMessage(err, 'Failed to load location issue.')
      });
    } finally {
      setLoading(false);
    }
  }, [
    issueId,
    submitIdHint,
    legacyForm,
    assignmentForm,
    setFlash,
    singleEntryField,
    systemDropdownValues
  ]);

  useEffect(() => {
    getIssuesConfig()
      .then((res) => setIssuesConfig({ ...DEFAULT_ISSUES_UI_CONFIG, ...(res?.config || {}) }))
      .catch(() => setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG));
  }, []);

  useEffect(() => {
    if (!isOpen || !issueId) return;
    setIsEditing(false);
    loadIssue();
  }, [isOpen, issueId, loadIssue]);

  const mapPhotosForManagement = useCallback(
    (photos: WorkflowIssue['issuePhotosBefore']): PhotoRow[] =>
      (photos || [])
        .filter((p) => !archivedPhotoIds.includes(Number(p.id)))
        .map((p) => ({
          id: Number(p.id),
          url: p.url || p.photo?.url || '',
          storage: {
            fileName: p.photo?.fileName || `Photo ${p.id}`,
            type: 'image'
          }
        })),
    [archivedPhotoIds]
  );

  const existingBeforePhotos = useMemo(
    () =>
      mapPhotosForManagement(
        issue?.issuePhotosBefore?.length
          ? issue.issuePhotosBefore
          : issue?.roomLevelIssuePhotos?.filter(
              (p) =>
                !(p as { photoType?: string }).photoType ||
                (p as { photoType?: string }).photoType === 'ISSUE_BEFORE'
            )
      ),
    [issue, mapPhotosForManagement]
  );

  const existingAfterPhotos = useMemo(
    () => mapPhotosForManagement(issue?.issuePhotosAfter),
    [issue?.issuePhotosAfter, mapPhotosForManagement]
  );

  const handleSave = legacyForm.handleSubmit(async (valueForm) => {
    if (!issueId) return;
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    const resolvedSubmitId = issue?.submitId;
    if (!resolvedSubmitId) {
      setFlash({ type: 'error', message: 'Missing issue context. Please reload.' });
      return;
    }

    try {
      const assignmentValues = assignmentForm.getValues();
      const allNewFiles = [...newBeforePhotos, ...newAfterPhotos];
      const photoTypes = [
        ...newBeforePhotos.map(() => 'ISSUE_BEFORE'),
        ...newAfterPhotos.map(() => 'ISSUE_AFTER')
      ];

      const assignmentPutFields = buildIssueAssignmentPutFields(assignmentValues);
      const workflowStatus = assignmentPutFields.status || valueForm.status;

      await updateRoomLevelIssue(
        {
          id: issueId,
          ...valueForm,
          ...assignmentPutFields,
          validated: valueForm.validated === 'Yes',
          resolutionDate: workflowStatus === 'ACTIVE' ? null : valueForm.resolutionDate,
          files: allNewFiles.length ? allNewFiles : undefined,
          photoTypes: allNewFiles.length ? JSON.stringify(photoTypes) : undefined,
          archivePhotoIds: archivedPhotoIds.length ? archivedPhotoIds : undefined
        },
        Number(resolvedSubmitId)
      );
      setFlash({ type: 'success', message: 'Location issue updated successfully.' });
      setIsEditing(false);
      setNewBeforePhotos([]);
      setNewAfterPhotos([]);
      setArchivedPhotoIds([]);
      onSaved?.();
      await loadIssue();
    } catch (err) {
      setFlash({
        type: 'error',
        message: getApiErrorMessage(err, 'Failed to update location issue.')
      });
    }
  });

  return {
    loading,
    issue,
    isEditing,
    issuesConfig,
    criticalityLevels,
    submitId,
    legacyForm,
    assignmentForm,
    existingBeforePhotos,
    existingAfterPhotos,
    newBeforePhotos,
    setNewBeforePhotos,
    newAfterPhotos,
    setNewAfterPhotos,
    archivedPhotoIds,
    setArchivedPhotoIds,
    handleSave,
    setIssue,
    reload: loadIssue
  };
};
